import { type QueryStatus, useQueries, useQuery } from '@tanstack/react-query';

import { fetchAllCompanySubsidiaries } from '../../../../../shared/api/fetchAllCompanySubsidiaries';
import { fetchCompanyByRcid } from '../../../../../shared/api/fetchCompanyByRcid';
import type { CompanySearchApiResult } from '../../../../../shared/api/fetchCompanySearchResults';
import { fetchCompanySubsidiaries } from '../../../../../shared/api/fetchCompanySubsidiaries';
import {
  CompanyMapperHttpError,
  fetchSubsidiariesOfCompanies,
} from '../../../../../shared/api/fetchSubsidiariesOfCompanies';
import { DataProviderQueryStatus } from '../types';

export const toSubsidiaryQueryStatus = (
  status: QueryStatus
): DataProviderQueryStatus => {
  if (status === 'error') return DataProviderQueryStatus.Error;
  if (status === 'success') return DataProviderQueryStatus.Success;
  return DataProviderQueryStatus.Fetching;
};

const MAPPER_QUERY_OPTIONS = {
  staleTime: Number.POSITIVE_INFINITY,
  retry: 1,
  retryDelay: 300,
  meta: { skipGlobalErrorHandling: true },
};

/**
 * One query per company-mapper call, keyed by company, so every card on the
 * Subsidiaries page — tree, counts, distributions — shares the same requests.
 */
export const companyQuery = (rcid: number) => ({
  ...MAPPER_QUERY_OPTIONS,
  queryKey: ['company-mapper', 'company', rcid],
  queryFn: ({ signal }: { signal: AbortSignal }) =>
    fetchCompanyByRcid(rcid, signal),
});

export const directSubsidiariesQuery = (rcid: number) => ({
  ...MAPPER_QUERY_OPTIONS,
  queryKey: ['company-mapper', 'subsidiaries', rcid],
  queryFn: ({ signal }: { signal: AbortSignal }) =>
    fetchCompanySubsidiaries(rcid, signal),
});

export const isMissingMapperRoute = (error: unknown) =>
  error instanceof CompanyMapperHttpError && error.status === 404;

export const subsidiariesOfCompaniesQuery = (rcids: readonly number[]) => ({
  ...MAPPER_QUERY_OPTIONS,
  retry: (failureCount: number, error: Error) =>
    failureCount < MAPPER_QUERY_OPTIONS.retry && !isMissingMapperRoute(error),
  queryKey: ['company-mapper', 'get-companies', rcids],
  queryFn: ({ signal }: { signal: AbortSignal }) =>
    fetchSubsidiariesOfCompanies(rcids, signal),
});

export const allSubsidiariesQuery = (rcid: number) => ({
  ...MAPPER_QUERY_OPTIONS,
  queryKey: ['company-mapper', 'all-subsidiaries', rcid],
  queryFn: ({ signal }: { signal: AbortSignal }) =>
    fetchAllCompanySubsidiaries(rcid, signal),
});

const distinctRcids = (entries: CompanySearchApiResult[]) => [
  ...new Set(entries.map((entry) => entry.rcid)),
];

/**
 * Every company under one, at any depth. The mapper walks ownership loops, so
 * the same company can come back twice; each is kept once.
 */
const descendantsQuery = (rcid: number) => ({
  ...allSubsidiariesQuery(rcid),
  select: distinctRcids,
});

const combineDescendantCounts = (
  results: { data?: number[]; status: QueryStatus; error: Error | null }[]
) => ({
  counts: results.map((result) => result.data?.length),
  statuses: results.map((result) => toSubsidiaryQueryStatus(result.status)),
  error: results.find((result) => result.error !== null)?.error ?? null,
});

/** How many companies sit under each one, at any depth, in the order given. */
export const useDescendantCounts = (rcids: number[]) =>
  useQueries({
    queries: rcids.map((rcid) => descendantsQuery(rcid)),
    combine: combineDescendantCounts,
  });

export const useCompany = (rcid: number | null) =>
  useQuery({
    ...companyQuery(rcid ?? 0),
    enabled: rcid !== null,
  });
